import { useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { format, parseISO, isPast } from 'date-fns';
import { ko } from 'date-fns/locale';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  IconButton,
  Typography,
  List,
  ListItem,
  TextField,
  Box,
  Collapse,
  Popover,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
// hooks
import useGoals from '../../hooks/useGoals';
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import { GoalAddDialog } from './';

export default function GoalEditDialog({ isOpen, closeDialog, companyId }) {
  const smUp = useResponsive('up', 'sm');
  const { goals, completeGoal, uncompleteGoal, updateGoal, deleteGoal } = useGoals(companyId);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editDueDate, setEditDueDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // 다이얼로그가 닫힐 때 완료 목록도 접기
  useEffect(() => {
    if (!isOpen) {
      setShowCompleted(false);
    }
  }, [isOpen]);

  const handleSelectGoal = (goal) => {
    setSelectedGoal(goal);
    setEditContent(goal.content);
    setEditDueDate(goal.due_date ? parseISO(goal.due_date) : null);
    setIsEditMode(true);
  };

  const handleCloseEdit = () => {
    setSelectedGoal(null);
    setIsEditMode(false);
    setEditContent('');
    setEditDueDate(null);
  };

  const handleCompleteToggle = async (goal) => {
    try {
      setIsLoading(true);
      if (goal.is_completed) {
        await uncompleteGoal(goal.id);
      } else {
        await completeGoal(goal.id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedGoal) return;
    
    try {
      setIsLoading(true);
      await updateGoal(selectedGoal.id, {
        content: editContent,
        due_date: editDueDate ? format(editDueDate, 'yyyy-MM-dd') : null,
      });
      handleCloseEdit();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (goal) => {
    // 편집 모드에서 호출된 경우
    if (!goal && selectedGoal) {
      goal = selectedGoal;
    }

    if (!goal) return;

    try {
      setIsLoading(true);
      await deleteGoal(goal.id);

      // 편집 모드였다면 닫기
      if (selectedGoal && selectedGoal.id === goal.id) {
        handleCloseEdit();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const incompleteGoals = goals.filter(goal => !goal.is_completed);
  const completedGoals = goals.filter(goal => goal.is_completed);

  if (isEditMode && selectedGoal) {
    return (
      <Dialog open={isOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm" fullScreen={!smUp}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">요청사항 수정</Typography>
            <IconButton onClick={handleCloseEdit} edge="end">
              <Iconify icon="ic:close" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="요청내용"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
            />
            <DatePicker
              label="기한"
              value={editDueDate}
              onChange={setEditDueDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              inputFormat="yyyy년 MM월 dd일"
              locale={ko}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="inherit" sx={{ px: 3 }} size="large">
            닫기
          </Button>
          <LoadingButton
            loading={isLoading}
            onClick={handleDelete}
            color="error"
            sx={{ px: 3 }}
            size="large"
          >
            삭제하기
          </LoadingButton>
          <LoadingButton
            loading={isLoading}
            onClick={handleUpdate}
            variant="contained"
            disabled={!editContent}
            sx={{ px: 3 }}
            size="large"
          >
            수정하기
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm" fullScreen={!smUp}>
        <DialogTitle variant="h6" sx={{ pb: 1 }}>
          요청사항 수정하기
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {goals.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                등록된 요청사항이 없습니다
              </Typography>
              <Button
                variant="contained"
                onClick={() => setIsAddDialogOpen(true)}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                요청사항 추가하기
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              {incompleteGoals.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, px: 1 }}>
                    진행 중 ({incompleteGoals.length})
                  </Typography>
                  <TransitionGroup>
                    {incompleteGoals.map((goal) => {
                      const isOverdue = goal.due_date && isPast(parseISO(goal.due_date));
                      return (
                        <Collapse key={goal.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 1.5,
                              mb: 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              ':hover': {
                                bgcolor: 'action.hover',
                                cursor: 'pointer',
                              },
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: isOverdue ? 'error.main' : 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  mb: 0.5,
                                }}
                              >
                                {goal.content}
                              </Typography>
                              {goal.due_date && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isOverdue ? 'error.main' : 'text.secondary',
                                    display: 'block',
                                  }}
                                >
                                  {format(parseISO(goal.due_date), 'MM월 dd일', { locale: ko })}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              color="primary"
                              sx={{
                                boxShadow: 'none',
                                flexShrink: 0,
                              }}
                              onClick={() => handleCompleteToggle(goal)}
                            >
                              완료
                            </Button>
                            <MoreMenu
                              actions={
                                <>
                                  <MenuItem onClick={() => handleSelectGoal(goal)} sx={{ height: '46px' }}>
                                    <Iconify icon="eva:edit-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                                    수정하기
                                  </MenuItem>
                                  <MenuItem onClick={() => handleDelete(goal)} sx={{ height: '46px' }}>
                                    <Iconify icon="eva:trash-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                                    삭제하기
                                  </MenuItem>
                                </>
                              }
                            />
                          </Box>
                        </Collapse>
                      );
                    })}
                  </TransitionGroup>
                </>
              )}

              {completedGoals.length > 0 && (
                <>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      mt: 2,
                      mb: 1.5,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    onClick={() => setShowCompleted(!showCompleted)}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        완료됨 ({completedGoals.length})
                      </Typography>
                      <Iconify
                        icon={showCompleted ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                        sx={{ color: 'text.secondary' }}
                      />
                    </Stack>
                  </Box>
                  {showCompleted && (
                    <TransitionGroup>
                      {completedGoals.map((goal) => (
                        <Collapse key={goal.id}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 2,
                              py: 1.5,
                              mb: 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'action.hover',
                              opacity: 0.6,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  color: 'text.disabled',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  mb: 0.5,
                                  textDecoration: 'line-through',
                                }}
                              >
                                {goal.content}
                              </Typography>
                              {goal.completed_at && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.disabled',
                                    display: 'block',
                                  }}
                                >
                                  {format(parseISO(goal.completed_at), 'MM월 dd일 완료', { locale: ko })}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              variant="outlined"
                              color="inherit"
                              sx={{
                                boxShadow: 'none',
                                flexShrink: 0,
                                color: 'text.secondary',
                              }}
                              onClick={() => handleCompleteToggle(goal)}
                            >
                              취소
                            </Button>
                          </Box>
                        </Collapse>
                      ))}
                    </TransitionGroup>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsAddDialogOpen(true)}
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            요청사항 추가
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDialog} variant="contained" size="large" sx={{ px: 4 }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      
      <GoalAddDialog
        isOpen={isAddDialogOpen}
        closeDialog={() => setIsAddDialogOpen(false)}
        companyId={companyId}
      />
    </>
  );
}

function MoreMenu({ actions }) {
  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    event.stopPropagation();
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          ...open && { bgcolor: 'action.hover' },
          borderRadius: 1
        }}
      >
        <SvgIconStyle
          src={`/icons/icon-dots-mono.svg`}
          sx={{ width: 20, height: 20, color: 'text.disabled' }}
        />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            overflow: 'inherit',
            width: 150,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'subtitle2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {actions}
      </Popover>
    </>
  );
}